import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { getProductionUnitLabel } from "~/constants/units";
import { useCompleteProductionBatch } from "~/hooks/production";
import { useToast } from "~/hooks/use-toast";
import type {
  BatchCompletionData,
  BatchIngredientActualData,
  ProductionBatch,
  ProductionBatchIngredient,
} from "~/types/production";
import { formatCurrency } from "~/utils/helpers";

interface BatchCompletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batch: ProductionBatch;
  userId: string;
  onSuccess: () => void;
}

export function BatchCompletionDialog({
  open,
  onOpenChange,
  batch,
  userId,
  onSuccess,
}: BatchCompletionDialogProps) {
  const { toast } = useToast();
  const completeMutation = useCompleteProductionBatch();

  const [actualQuantity, setActualQuantity] = useState(
    batch.actual_quantity ?? batch.target_quantity
  );
  const [notes, setNotes] = useState(batch.notes || "");
  const [ingredients, setIngredients] = useState<BatchIngredientActualData[]>(
    []
  );

  // Recipe per-box quantities, keyed by recipe_ingredient_id. Completion
  // amounts are derived from the live recipe (qty_per_box * waste_factor *
  // boxes produced) so they stay correct even when the batch's frozen
  // planned_quantity snapshot is stale (e.g. the target was edited after
  // creation).
  const recipeById = useMemo(() => {
    const map = new Map<
      string,
      { quantity_per_box: number; waste_factor: number }
    >();
    for (const rl of batch.recipe?.ingredients ?? []) {
      map.set(rl.id, {
        quantity_per_box: rl.quantity_per_box,
        waste_factor: rl.waste_factor ?? 1,
      });
    }
    return map;
  }, [batch.recipe]);

  // Per-box factor for an ingredient line: use the recipe for recipe-backed,
  // non-override lines; otherwise fall back to the frozen snapshot ratio so
  // manual overrides and off-recipe lines keep their proportional scaling.
  const getPerBox = useCallback(
    (ing: ProductionBatchIngredient) => {
      const rl = ing.recipe_ingredient_id
        ? recipeById.get(ing.recipe_ingredient_id)
        : undefined;
      if (rl && !ing.is_override) {
        return rl.quantity_per_box * rl.waste_factor;
      }
      return batch.target_quantity > 0
        ? ing.planned_quantity / batch.target_quantity
        : 0;
    },
    [recipeById, batch.target_quantity]
  );

  useEffect(() => {
    if (batch.ingredients) {
      setIngredients(
        batch.ingredients.map((ing) => ({
          id: ing.id,
          actual_quantity: ing.planned_quantity,
          oil_type_id: ing.oil_type_id || undefined,
          raw_material_id: ing.raw_material_id || undefined,
        }))
      );
    }
    setActualQuantity(batch.actual_quantity ?? batch.target_quantity);
    setNotes(batch.notes || "");
  }, [batch, open]);

  // Auto-scale ingredients when actual quantity changes
  useEffect(() => {
    if (batch.target_quantity > 0 && batch.ingredients) {
      setIngredients((prev) =>
        prev.map((ing) => {
          const originalIng = batch.ingredients?.find((i) => i.id === ing.id);
          if (originalIng) {
            return {
              ...ing,
              actual_quantity: getPerBox(originalIng) * actualQuantity,
            };
          }
          return ing;
        })
      );
    }
  }, [actualQuantity, batch.target_quantity, batch.ingredients, getPerBox]);

  const handleIngredientChange = (id: string, actual_quantity: number) => {
    setIngredients((prev) =>
      prev.map((ing) => (ing.id === id ? { ...ing, actual_quantity } : ing))
    );
  };

  // Calculate total cost
  const calculateTotalCost = () => {
    let totalClp = 0;
    let totalUsd = 0;

    if (!batch.ingredients) return { totalClp, totalUsd };

    for (const ing of batch.ingredients) {
      const actualIng = ingredients.find((i) => i.id === ing.id);
      const quantity = actualIng?.actual_quantity || ing.planned_quantity;
      totalClp += ing.unit_cost_clp * quantity;
      if (ing.unit_cost_usd) {
        totalUsd += ing.unit_cost_usd * quantity;
      }
    }

    return { totalClp, totalUsd };
  };

  const { totalClp, totalUsd } = calculateTotalCost();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const completionData: BatchCompletionData = {
      actual_quantity: actualQuantity,
      actual_cost_clp: totalClp,
      actual_cost_usd: totalUsd,
      completed_by: userId,
      ingredients,
      notes: notes || undefined,
    };

    completeMutation.mutate(
      { id: batch.id, completionData },
      {
        onSuccess: () => {
          onSuccess();
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Completar Lote: {batch.batch_number}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="actual_quantity">
              Cantidad Real Producida (
              {getProductionUnitLabel(batch.production_unit, 2)})
            </Label>
            <Input
              id="actual_quantity"
              type="number"
              min="0"
              value={actualQuantity}
              onChange={(e) => setActualQuantity(parseInt(e.target.value) || 0)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Objetivo: {batch.target_quantity}{" "}
              {getProductionUnitLabel(
                batch.production_unit,
                batch.target_quantity
              )}
            </p>
          </div>

          {batch.ingredients && batch.ingredients.length > 0 && (
            <div className="space-y-3">
              <Label className="text-base font-semibold">
                Cantidades Reales de Ingredientes
              </Label>
              <div className="space-y-2">
                {batch.ingredients.map((ing) => {
                  const isOil = !!ing.oil_type_id;
                  const name = isOil
                    ? ing.oil_type?.name
                    : ing.raw_material?.name;
                  const unit = isOil ? "kg" : ing.raw_material?.unit || "units";
                  const actualIng = ingredients.find((i) => i.id === ing.id);
                  const plannedQuantity = getPerBox(ing) * batch.target_quantity;

                  return (
                    <Card key={ing.id}>
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <p className="font-medium">{name}</p>
                            <p className="text-xs text-muted-foreground">
                              Planificado: {plannedQuantity.toFixed(2)} {unit}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="any"
                              min="0"
                              className="w-32"
                              value={actualIng?.actual_quantity || ""}
                              onChange={(e) =>
                                handleIngredientChange(
                                  ing.id,
                                  parseFloat(e.target.value) || 0
                                )
                              }
                            />
                            <span className="w-8 text-sm text-muted-foreground">
                              {unit}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Notas de Completación</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones sobre la producción..."
              rows={3}
            />
          </div>

          <Card className="bg-muted/50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Costo Total Real:</span>
                <span className="text-xl font-bold">
                  ${formatCurrency(totalClp)}
                </span>
              </div>
              {actualQuantity > 0 && (
                <div className="mt-1 flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    Costo por{" "}
                    {batch.production_unit === "UNI" ? "unidad" : "caja"}:
                  </span>
                  <span>${formatCurrency(totalClp / actualQuantity)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={completeMutation.isPending}>
              {completeMutation.isPending ? "Completando..." : "Completar Lote"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
